import {
  AdminSpinner,
  DebtRepayment,
  DebtRepaymentService,
  Disclaimer,
  Div,
  ListSpinner,
  Modal,
  TableHeader,
  lightTheme,
} from '@vgrubs/components';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  SERVICE_PLAN_TITLES,
  planService,
} from '../helpers/repaymentPlanLabels';
import { useLocationRepayments } from '../hooks/useLocationRepayments';
import { DebtRepaymentRow, REPAYMENT_COLUMNS } from './DebtRepaymentRow';

type Props = {
  isOpen: boolean;
  locationId: number;
  /**
   * Only this service's repayments. The route pages both services together
   * with one count, so the list is filtered here and the count left out.
   */
  service?: DebtRepaymentService;
  /**
   * What the location detail already has (its 5 newest), shown while the
   * first page loads and instead of it on a backend without the paged route.
   */
  latest: DebtRepayment[];
  /** How many there are in all, for the subtitle until the list says. */
  total: number;
  onClose: () => void;
};

/**
 * Every repayment of a location, behind the plan card's "Repayment history",
 * as wide as the table needs. Pages of 20 load as the list scrolls to its
 * end; the modal scrolls on its own.
 */
export const RepaymentsModal = ({
  isOpen,
  locationId,
  service,
  latest,
  total,
  onClose,
}: Props) => {
  const list = useLocationRepayments(locationId, isOpen);
  const { ref, inView } = useInView();

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Before Oct 8, 2026 the paged route did not exist (404): the detail's own
  // list is then all there is to show.
  const loaded = list.isError || list.isLoading ? latest : list.repayments;
  const rows = service
    ? loaded.filter((repayment) => planService(repayment) === service)
    : loaded;
  const count = list.total ?? total;
  // Filtered, a page can bring none of this service's rows; the spinner at
  // the end stays in view and the next page follows on its own.
  const subtitle = service
    ? SERVICE_PLAN_TITLES[service]
    : `${count} ${count === 1 ? 'repayment' : 'repayments'}`;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      width="1000px"
      header="Repayment history"
      subtitle={subtitle}
    >
      {/* The modal centres its text; a table reads left-aligned, as on the
          page. */}
      <Div style={{ textAlign: 'left' }}>
        <TableHeader columns={REPAYMENT_COLUMNS} gap="24px" />
        {rows.map((repayment) => (
          <DebtRepaymentRow key={repayment.id} repayment={repayment} />
        ))}
        {list.isLoading && latest.length === 0 && <AdminSpinner />}
        {!list.isLoading && !hasNextPage && rows.length === 0 && (
          <Disclaimer $m="16px 0" $center $color={lightTheme.lightGray}>
            Nothing collected yet.
          </Disclaimer>
        )}
        {hasNextPage && (
          <ListSpinner loading={isFetchingNextPage} observerRef={ref} />
        )}
      </Div>
    </Modal>
  );
};
